/**
 * Pure view-state helpers for the messaging channel cards and their dialogs.
 * Components render from these; they hold no testable logic themselves.
 */

export const MESSAGING_PLATFORMS = [
  'slack',
  'teams',
  'telegram',
  'whatsapp',
  'imessage',
] as const;

export type MessagingPlatform = (typeof MESSAGING_PLATFORMS)[number];

/** Channels connected through a provider sign-in (one-click connect). */
export const MESSAGING_CONNECT_PLATFORMS = [
  'slack',
  'teams',
  'telegram',
] as const;

export type MessagingConnectPlatform =
  (typeof MESSAGING_CONNECT_PLATFORMS)[number];

/** Channels covered by one verified phone number. */
export type MessagingPhoneChannel = 'imessage' | 'whatsapp';

/** Proper names; never translated. */
export const MESSAGING_CHANNEL_NAMES: Record<MessagingPlatform, string> = {
  slack: 'Slack',
  teams: 'Microsoft Teams',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  imessage: 'iMessage',
};

export type MessagingHandles = {
  telegramBotUsername?: string;
  whatsappNumber?: string;
  imessageNumber?: string;
  slackInstallUrl?: string;
  teamsInstallUrl?: string;
};

export type MessagingAvailability = Record<MessagingPlatform, boolean> & {
  handles: MessagingHandles;
};

export type MessagingIdentity<Id extends string = string> = {
  _id: Id;
  platform: string;
  displayName?: string | null;
  linkedAt: number;
};

export type MessagingConnectRow<Id extends string = string> = {
  platform: MessagingConnectPlatform;
  status: 'unavailable' | 'connected' | 'disconnected';
  /** Live identities for this platform, newest first. */
  identities: MessagingIdentity<Id>[];
  canConnect: boolean;
  /** "Add IMAI to your workspace" / "Add IMAI to Teams" link. */
  installUrl: string | null;
  /** "Open in Telegram" link, shown once connected. */
  openUrl: string | null;
};

export function isMessagingConnectPlatform(
  value: string | null | undefined
): value is MessagingConnectPlatform {
  return (MESSAGING_CONNECT_PLATFORMS as readonly string[]).includes(
    value ?? ''
  );
}

/** Only absolute https URLs are ever rendered as links. */
export function toSafeHttpsUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * The connect URL Convex returns: https anywhere, plain http only for a local
 * messaging app during development.
 */
export function isSafeConnectUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'https:') return true;
    return parsed.protocol === 'http:' && LOCAL_HOSTS.has(parsed.hostname);
  } catch {
    return false;
  }
}

/** `https://t.me/<bot>` for a bot username with or without `@`. */
export function buildTelegramBotUrl(
  username: string | null | undefined
): string | null {
  const bot = username?.trim().replace(/^@/, '');
  if (!bot || !/^[A-Za-z0-9_]{3,64}$/.test(bot)) return null;
  return `https://t.me/${bot}`;
}

function phoneDigits(number: string | null | undefined): string | null {
  const digits = number?.replace(/\D/g, '') ?? '';
  return digits.length >= 6 && digits.length <= 15 ? digits : null;
}

/**
 * "Message IMAI" links for IMAI's own numbers: `sms:+<digits>&body=` opens
 * iMessage, `https://wa.me/<digits>` opens WhatsApp. Null when the channel is
 * off or its number is missing. The empty body keeps the SMS link in the
 * compose shape the desktop app lets through (`isSafeSmsComposeUrl`).
 */
export function buildMessageImaiLinks(
  availability: MessagingAvailability,
  selectedChannel?: MessagingPhoneChannel
): Record<MessagingPhoneChannel, string | null> {
  const imessage =
    availability.imessage && selectedChannel !== 'whatsapp'
      ? phoneDigits(availability.handles.imessageNumber)
      : null;
  const whatsapp =
    availability.whatsapp && selectedChannel !== 'imessage'
      ? phoneDigits(availability.handles.whatsappNumber)
      : null;
  return {
    imessage: imessage ? `sms:+${imessage}&body=` : null,
    whatsapp: whatsapp ? `https://wa.me/${whatsapp}` : null,
  };
}

/**
 * One row per connect platform in a fixed order. A connected identity always
 * shows, even if the channel was switched off later, so the user can still
 * disconnect it.
 */
export function buildMessagingConnectRows<Id extends string>(args: {
  availability: MessagingAvailability;
  identities: readonly MessagingIdentity<Id>[];
}): MessagingConnectRow<Id>[] {
  const { availability } = args;
  return MESSAGING_CONNECT_PLATFORMS.map((platform) => {
    const identities = args.identities
      .filter((identity) => identity.platform === platform)
      .sort((a, b) => b.linkedAt - a.linkedAt);
    const available = availability[platform] === true;
    const connected = identities.length > 0;
    const installUrl =
      !available || platform === 'telegram'
        ? null
        : toSafeHttpsUrl(
            platform === 'slack'
              ? availability.handles.slackInstallUrl
              : availability.handles.teamsInstallUrl
          );
    return {
      platform,
      status: connected
        ? 'connected'
        : available
          ? 'disconnected'
          : 'unavailable',
      identities,
      canConnect: available && !connected,
      installUrl,
      openUrl:
        platform === 'telegram' && connected && available
          ? buildTelegramBotUrl(availability.handles.telegramBotUsername)
          : null,
    };
  });
}

export function isMessagingPlatform(
  value: string | null | undefined
): value is MessagingPlatform {
  return (MESSAGING_PLATFORMS as readonly string[]).includes(value ?? '');
}

export type MessagingCardState = {
  /** The card opens: the channel is set up on this deployment, or the member
   * still has an account linked there to disconnect. */
  available: boolean;
  /** Shown under "Connected to this workspace". */
  connected: boolean;
};

/**
 * State for each channel's card in the connector catalog. A card shows Soon
 * until the deployment has the channel's keys, opens once they are added, and
 * moves to Connected only when the member finishes that channel’s setup.
 */
export function buildMessagingCardStates(args: {
  availability: MessagingAvailability;
  identities: readonly Pick<MessagingIdentity, 'platform'>[];
  connectedPhoneChannels: readonly MessagingPhoneChannel[];
}): Record<MessagingPlatform, MessagingCardState> {
  const { availability, identities, connectedPhoneChannels } = args;
  const phoneCard = (channel: MessagingPhoneChannel): MessagingCardState => ({
    available: availability[channel],
    connected:
      connectedPhoneChannels.includes(channel) && availability[channel],
  });
  const connectCard = (
    platform: MessagingConnectPlatform
  ): MessagingCardState => {
    const connected = identities.some(
      (identity) => identity.platform === platform
    );
    return { available: availability[platform] || connected, connected };
  };
  return {
    whatsapp: phoneCard('whatsapp'),
    imessage: identities.some((identity) => identity.platform === 'imessage')
      ? { available: true, connected: true }
      : phoneCard('imessage'),
    slack: connectCard('slack'),
    teams: connectCard('teams'),
    telegram: connectCard('telegram'),
  };
}

/** Only confirmed connections appear in Your IMAI; setup lives in Connectors. */
export function channelHistoryMenuPlatforms(
  channels: readonly { platform: MessagingPlatform; connected: boolean }[] = []
): MessagingPlatform[] {
  return [
    ...new Set(
      channels.flatMap((channel) =>
        channel.connected ? [channel.platform] : []
      )
    ),
  ];
}

export function channelHistoryNeighbors(
  current: 'main' | MessagingPlatform,
  channels: readonly MessagingPlatform[] = []
) {
  const choices: ('main' | MessagingPlatform)[] = [
    'main',
    ...new Set(channels),
  ];
  const index = Math.max(0, choices.indexOf(current));
  return {
    previous: choices[(index + choices.length - 1) % choices.length] ?? 'main',
    next: choices[(index + 1) % choices.length] ?? 'main',
  };
}

/** Each phone channel is activated by a message from its own provider. */
export function phoneActivationAvailability(
  platform: MessagingPhoneChannel,
  availability: MessagingAvailability
): boolean {
  return availability[platform];
}
